import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, FormControl, FormControlLabel, Checkbox, MenuItem, Select, InputLabel, TextField, Typography, Stack, Chip, Switch, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Snackbar, Alert, Autocomplete } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { excelMappingApi } from '../api/excelMapping';
import type { ExcelMappingConfig } from '../api/excelMapping';
import { exportCsvApi } from '../api/exportCsv';
import axiosClient from '../api/axiosClient';
import type { ExportCsvConfigDto } from '../api/exportCsv';
import useAuth from '../hooks/useAuth';
import { listUsers } from '../api/users';
import type { UserDto } from '../api/users';
import { useTranslation } from 'react-i18next';

const ExportCsvPage: React.FC = () => {
  useAuth();
  const { t } = useTranslation();
  const [mappings, setMappings] = useState<ExcelMappingConfig[]>([]);
  const [mappingId, setMappingId] = useState<number | ''>('');
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [detailedFields, setDetailedFields] = useState<Array<{ path: string; example?: string }>>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [delimiter, setDelimiter] = useState(';');
  const [configs, setConfigs] = useState<ExportCsvConfigDto[]>([]);
  const [configName, setConfigName] = useState('');
  const [configDescription, setConfigDescription] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configFilter, setConfigFilter] = useState<'all' | 'own' | 'shared'>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<ExportCsvConfigDto | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsShared, setEditIsShared] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [currentConfigId, setCurrentConfigId] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  // Filtros importados del Dashboard
  const [filterStart, setFilterStart] = useState<string>('');
  const [filterEnd, setFilterEnd] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'todos' | 'correcto' | 'incorrecto'>('todos');
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [filterCampaign, setFilterCampaign] = useState<string>('');
  const [adminUsers, setAdminUsers] = useState<UserDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [filterUserId, setFilterUserId] = useState<string>('');
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      const resp = await excelMappingApi.getConfigs();
      const list = (resp?.data ?? resp) as ExcelMappingConfig[];
      setMappings(list);
    })();
    // Cargar campañas y usuarios admin
    (async () => {
      try {
        const { data } = await axiosClient.get('/dashboard/campaigns');
        setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []);
      } catch {}
    })();
    (async () => {
      try {
        if (user?.role === 'admin') {
          const res = await listUsers({ page: 1, limit: 100 });
          setAdminUsers(res.data || []);
        }
      } catch {}
    })();
  }, [user?.role]);

  useEffect(() => {
    if (!mappingId) {
      setAvailableFields([]);
      setConfigs([]);
      setSelectedFields([]);
      setCurrentConfigId(null);
      return;
    }
    (async () => {
      const [fields, detailed, cfgs] = await Promise.all([
        exportCsvApi.getAvailableFields(Number(mappingId)),
        exportCsvApi.getAvailableFieldsDetailed(Number(mappingId)),
        exportCsvApi.listConfigs(Number(mappingId)),
      ]);
      setAvailableFields(fields);
      setDetailedFields(detailed);
      setConfigs(cfgs);
      // Resetear selección y config aplicada al cambiar de mapping
      setSelectedFields([]);
      setCurrentConfigId(null);
    })();
  }, [mappingId]);

  const toggleField = (field: string) => {
    setSelectedFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  };

  const saveConfig = async () => {
    if (!mappingId || selectedFields.length === 0 || !configName.trim()) return;
    setLoading(true);
    try {
      const payload: Omit<ExportCsvConfigDto, 'id' | 'createdAt' | 'updatedAt'> = {
        name: configName.trim(),
        description: configDescription || null,
        selectedFields,
        delimiter,
        includeHeaders,
        isShared,
        mappingConfigId: Number(mappingId),
      };
      const saved = await exportCsvApi.createConfig(payload);
      setConfigs(prev => [saved, ...prev]);
      setConfigName('');
      setConfigDescription('');
      setIsShared(false);
      setSnackbarMsg('Configuración guardada');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const applyConfig = (cfgId: number) => {
    const cfg = configs.find(c => c.id === cfgId);
    if (!cfg) return;
    setSelectedFields(cfg.selectedFields || []);
    setDelimiter(cfg.delimiter || ',');
    setIncludeHeaders(cfg.includeHeaders ?? true);
    setIsShared(Boolean(cfg.isShared));
    setCurrentConfigId(cfg.id as number);
  };

  const canDownload = useMemo(() => Boolean(mappingId) && selectedFields.length > 0, [mappingId, selectedFields]);

  const filteredConfigs = useMemo(() => {
    if (configFilter === 'own') return configs.filter(c => !c.isShared);
    if (configFilter === 'shared') return configs.filter(c => c.isShared);
    return configs;
  }, [configs, configFilter]);

  const openDeleteDialog = (cfgId: number) => {
    const cfg = configs.find(c => c.id === cfgId);
    if (!cfg || cfg.isShared) return;
    setPendingDelete({ id: cfgId, name: cfg.name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setLoading(true);
    try {
      await exportCsvApi.deleteConfig(pendingDelete.id);
      setConfigs(prev => prev.filter(c => c.id !== pendingDelete.id));
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setPendingDelete(null);
    }
  };

  const openEditDialog = (cfgId: number) => {
    const cfg = configs.find(c => c.id === cfgId);
    if (!cfg || cfg.isShared) return; // sólo propias editables
    setPendingEdit(cfg);
    setEditName(cfg.name || '');
    setEditDescription(cfg.description || '');
    setEditIsShared(Boolean(cfg.isShared));
    setEditDialogOpen(true);
  };

  const confirmEdit = async () => {
    if (!pendingEdit) return;
    setLoading(true);
    try {
      const updated = await exportCsvApi.updateConfig(pendingEdit.id as number, {
        name: editName,
        description: editDescription,
        isShared: editIsShared,
      });
      setConfigs(prev => prev.map(c => (c.id === updated.id ? { ...c, ...updated } : c)));
    } finally {
      setLoading(false);
      setEditDialogOpen(false);
      setPendingEdit(null);
    }
  };

  const download = async () => {
    if (!canDownload) return;
    setLoading(true);
    try {
      const payload: any = { mappingConfigId: Number(mappingId), selectedFields, delimiter, includeHeaders };
      if (filterStart) payload.startDate = filterStart;
      if (filterEnd) payload.endDate = filterEnd;
      if (filterStatus && filterStatus !== 'todos') payload.status = filterStatus;
      if (filterCampaign) payload.campaign = filterCampaign;
      if (filterUserId) payload.userId = Number(filterUserId);
      const blob = await exportCsvApi.download(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${mappingId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('exportCsv.title', 'Exportación a CSV')}</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={2}>
            <FormControl fullWidth sx={{ minWidth: 260 }} size="small">
              <InputLabel id="mapping-label">{t('exportCsv.mapping', 'Configuración de Mapping')}</InputLabel>
              <Select labelId="mapping-label" label={t('exportCsv.mapping', 'Configuración de Mapping')} value={mappingId} onChange={(e) => setMappingId(e.target.value as number | '')}>
                {mappings.map(m => (
                  <MenuItem key={m.id} value={m.id as number}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label={t('exportCsv.delimiter', 'Separador')} value={delimiter} onChange={e => setDelimiter(e.target.value)} sx={{ maxWidth: 160 }} />
            <FormControlLabel control={<Checkbox checked={includeHeaders} onChange={(_, v) => setIncludeHeaders(v)} />} label={t('exportCsv.includeHeaders', 'Incluir cabeceras')} />
          </Stack>
        </CardContent>
      </Card>

      {/* Filtros (como en Dashboard) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('common.filters', 'Filtros')}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
            <TextField label={t('common.from', 'Desde')} type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label={t('common.to', 'Hasta')} type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="status-label">{t('common.status', 'Estado')}</InputLabel>
              <Select labelId="status-label" label={t('common.status', 'Estado')} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
                <MenuItem value="todos">{t('common.all', 'Todos')}</MenuItem>
                <MenuItem value="correcto">{t('common.correct', 'Correcto')}</MenuItem>
                <MenuItem value="incorrecto">{t('common.incorrect', 'Incorrecto')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="campaign-label">{t('dashboard.campaign', 'Campaña')}</InputLabel>
              <Select labelId="campaign-label" label={t('dashboard.campaign', 'Campaña')} value={filterCampaign} onChange={(e) => setFilterCampaign(e.target.value)}>
                <MenuItem value="">{t('common.allParen', '(Todas)')}</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Autocomplete de usuario si hay lista (podemos poblarla más tarde) */}
            {user?.role === 'admin' && adminUsers.length > 0 && (
              <Autocomplete
                options={adminUsers}
                getOptionLabel={(option: UserDto) => option?.username ?? ''}
                isOptionEqualToValue={(opt, val) => (opt as UserDto)?.id === (val as UserDto)?.id}
                value={selectedUser}
                onChange={(_, val) => {
                  setSelectedUser(val);
                  setFilterUserId(val ? String(val.id) : '');
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('dashboard.userUsername', 'Usuario (username)')} placeholder={t('dashboard.selectUser', 'Selecciona usuario')} />
                )}
                sx={{ minWidth: 260 }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('exportCsv.availableFields', 'Campos disponibles')}</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {(detailedFields.length > 0 ? detailedFields.map(df => df.path) : availableFields).map(f => (
              <Tooltip key={f} title={detailedFields.find(df => df.path === f)?.example || ''}>
                <Chip label={f} color={selectedFields.includes(f) ? 'primary' : 'default'} onClick={() => toggleField(f)} />
              </Tooltip>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('exportCsv.saveConfig', 'Guardar configuración')}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField fullWidth label={t('common.name', 'Nombre')} value={configName} onChange={e => setConfigName(e.target.value)} />
            <TextField fullWidth label={t('common.description', 'Descripción')} value={configDescription} onChange={e => setConfigDescription(e.target.value)} />
            <FormControlLabel control={<Switch checked={isShared} onChange={(_, v) => setIsShared(v)} />} label={t('exportCsv.shared', 'Compartida')} />
            <Button variant="contained" disabled={loading} onClick={saveConfig}>{t('common.save', 'Guardar')}</Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('exportCsv.myConfigs', 'Mis configuraciones')}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <InputLabel id="cfg-filter-label">{t('common.filter', 'Filtro')}</InputLabel>
              <Select labelId="cfg-filter-label" fullWidth value={configFilter} onChange={(e) => setConfigFilter(e.target.value as 'all' | 'own' | 'shared')}>
                <MenuItem value="all">{t('common.all', 'Todas')}</MenuItem>
                <MenuItem value="own">{t('exportCsv.own', 'Propias')}</MenuItem>
                <MenuItem value="shared">{t('exportCsv.shared', 'Compartidas')}</MenuItem>
              </Select>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {filteredConfigs.map(c => (
              <Stack key={c.id} direction="row" alignItems="center" gap={0.5}>
                <Tooltip title={c.isShared ? t('exportCsv.shared', 'Compartida') : t('exportCsv.own', 'Propia')}>
                  <Chip
                    label={c?.name || `Config ${c.id}`}
                    color={c.isShared ? 'secondary' : 'default'}
                    variant={c.isShared ? 'outlined' : 'filled'}
                    onClick={() => applyConfig(c.id as number)}
                    onDelete={!c.isShared ? () => openDeleteDialog(c.id as number) : undefined}
                    deleteIcon={!c.isShared ? <Delete /> : undefined}
                  />
                </Tooltip>
                {!c.isShared && (
                  <Tooltip title={t('common.edit', 'Editar')}>
                    <span>
                      <IconButton size="small" onClick={() => openEditDialog(c.id as number)} disabled={loading}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirmDelete', 'Confirmar eliminación')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('exportCsv.confirmDeleteText', '¿Seguro que quieres eliminar la configuración')}
            {pendingDelete ? ` "${pendingDelete.name}"` : ''}? {t('common.cannotUndo', 'Esta acción no se puede deshacer.')} 
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>{t('common.cancel', 'Cancelar')}</Button>
          <Button color="error" onClick={confirmDelete} disabled={loading}>{t('common.delete', 'Eliminar')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('exportCsv.editConfig', 'Editar configuración')}</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mt: 1 }}>
            <TextField fullWidth label={t('common.name', 'Nombre')} value={editName} onChange={e => setEditName(e.target.value)} />
            <TextField fullWidth label={t('common.description', 'Descripción')} value={editDescription} onChange={e => setEditDescription(e.target.value)} />
          </Stack>
          <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={editIsShared} onChange={(_, v) => setEditIsShared(v)} />} label={t('exportCsv.shared', 'Compartida')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>{t('common.cancel', 'Cancelar')}</Button>
          <Button variant="contained" onClick={confirmEdit} disabled={loading || !editName.trim()}>{t('common.save', 'Guardar')}</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('exportCsv.fieldsOrder', 'Orden de campos')}</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedFields.map((f, idx) => (
              <Stack
                key={f}
                direction="row"
                alignItems="center"
                gap={0.5}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => {
                  if (dragIndex === null || dragIndex === idx) return;
                  setSelectedFields(prev => {
                    const copy = [...prev];
                    const [moved] = copy.splice(dragIndex, 1);
                    copy.splice(idx, 0, moved);
                    return copy;
                  });
                  setDragIndex(null);
                }}
              >
                <Chip label={`${idx + 1}. ${f}`} />
                <Stack direction="row" gap={0.5}>
                  <Button size="small" variant="outlined" disabled={idx === 0} onClick={() => setSelectedFields(prev => {
                    const copy = [...prev];
                    const t = copy[idx - 1];
                    copy[idx - 1] = copy[idx];
                    copy[idx] = t;
                    return copy;
                  })}>{t('common.moveUp', 'Subir')}</Button>
                  <Button size="small" variant="outlined" disabled={idx === selectedFields.length - 1} onClick={() => setSelectedFields(prev => {
                    const copy = [...prev];
                    const t = copy[idx + 1];
                    copy[idx + 1] = copy[idx];
                    copy[idx] = t;
                    return copy;
                  })}>{t('common.moveDown', 'Bajar')}</Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
          {currentConfigId && (configs.find(c => c.id === currentConfigId)?.isShared ? false : true) && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await exportCsvApi.updateConfig(currentConfigId as number, {
                      selectedFields,
                      delimiter,
                      includeHeaders,
                      isShared,
                    });
                    setSnackbarMsg(t('common.changesSaved', 'Cambios guardados'));
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                  } catch (e) {
                    console.warn('No se pudo guardar cambios de la configuración seleccionada', e);
                    setSnackbarMsg(t('common.cannotSaveChanges', 'No se pudo guardar cambios'));
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {t('exportCsv.saveChangesInConfig', 'Guardar cambios en configuración')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box>
        <Button variant="contained" onClick={download} disabled={!canDownload || loading}>{t('exportCsv.downloadCsv', 'Descargar CSV')}</Button>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExportCsvPage;


